<template>
  <div
    class="asc-vision-grid--holder"
    :class="[disabled ? 'asc-vision-grid--holder--disabled' : null]"
  >
    <div v-if="disabled" class="asc-vision-grid--disabledOverlay"></div>
    <asc-ag-grid-filter
      :key="`asc-vision-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="updatedColumnDefs"
      :row-data="values"
      :disabled="disabled"
      :page-size-selected="pageSizeSelected"
      :page-sizes="pageSizes"
      :selections="selectedRows"
      :get-row-id="getRowId"
      grid-size="auto"
      @onSelectionChanged="handleSelectionChange"
      @onRowSelected="handleRowSelected"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscTooltipCellRenderer,
  AscAgGridDateFloatFilter,
  AscAgGridAutocompleteFloatFilter,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import _ from 'lodash'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscVisionGrid',

  components: {
    AscAgGridFilter,
    AscTooltipCellRenderer,
    AscAgGridDateFloatFilter,
    AscAgGridAutocompleteFloatFilter,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    columnDefs: {
      type: Array,
      required: true,
      default: () => [],
    },
    isEditable: {
      type: Boolean,
      required: true,
      default: true,
    },
    selectRowsByDefault: {
      type: Boolean,
      default: false,
    },
    showMarkerColumn: {
      type: Boolean,
      default: false,
    },
    markerColumnColor: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pageSizeSelected: {
      type: Number,
      default: 25,
    },
    pageSizes: {
      type: Array,
      default: () => [],
    },
    showEyeballColumn: {
      type: Boolean,
      default: true,
    },
    readOnlyEyeballColumn: {
      type: Boolean,
      default: false,
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    getRowId: {
      type: Function,
      default: undefined,
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          sortable: false,
          tooltipComponent: 'AscTooltipCellRenderer',
          tooltipValueGetter: param => {
            if (param.value) {
              const textWidth = this.calculateTextWidth(
                param.value,
                '16px Material Design Icons',
              )
              return textWidth > param.column.actualWidth ? param.value : null
            }
            return null
          },
        },
        context: {
          componentParent: this,
        },
        domLayout: 'autoHeight',
        rowSelection: 'multiple',
        paginationPageSize: 10,
        suppressAutoSize: true,
        suppressNoRowsOverlay: true,
        suppressRowClickSelection: true,
        isExternalFilterPresent:
          !this.isEditable || this.disabled
            ? false
            : this.checkForInternalUserFilter,
        doesExternalFilterPass: this.filterSelectedForInternalUser,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onRowDataChanged: this.handleRowDataChanged,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values
    },
    updatedColumnDefs: function () {
      const newColumnDefs = this.columnDefs.map(columnDefObj => {
        if (_.has(columnDefObj, 'floatingFilterComponent')) {
          if (
            columnDefObj.floatingFilterComponent === 'AscAgGridDateFloatFilter'
          ) {
            _.assign(columnDefObj, {
              floatingFilterComponentParams: {
                suppressFilterButton: true,
                items: this.values,
              },
              comparator: this.sortDate,
            })
          }

          if (
            columnDefObj.floatingFilterComponent ===
            'AscAgGridAutocompleteFloatFilter'
          ) {
            _.assign(columnDefObj, {
              floatingFilterComponentParams: {
                ids: [],
                items: this.getUniqueItems(columnDefObj.field),
                suppressFilterButton: true,
              },
            })
          }
        }
        return {
          ...columnDefObj,
          sortable: true,
          suppressMenu: true,
          resizable: false,
          editable: false,
          suppressMovable: true,
        }
      })

      if (this.readOnlyEyeballColumn && this.showEyeballColumn) {
        newColumnDefs.unshift({
          headerName: '',
          field: '',
          width: 40,
          maxWidth: 40,
          resizable: false,
          checkboxSelection: false,
          suppressMovable: true,
          suppressSizeToFit: true,
          cellRenderer: () =>
            `<i aria-hidden="true" class="asc-vision-grid-icon"></i>`,
        })
      } else if ((this.isEditable || this.disabled) && this.showEyeballColumn) {
        newColumnDefs.unshift({
          headerName: '',
          field: '',
          width: 40,
          maxWidth: 40,
          resizable: false,
          checkboxSelection: true,
          suppressMovable: true,
          suppressSizeToFit: true,
          tooltipValueGetter: () => 'Click to toggle visibility on/off',
        })
      }

      if (this.showMarkerColumn) {
        newColumnDefs.unshift({
          headerName: '',
          field: '',
          width: 12,
          maxWidth: 12,
          checkboxSelection: false,
          sortable: false,
          suppressMenu: true,
          suppressMovable: true,
          resizable: false,
          suppressSizeToFit: true,
          cellStyle: { backgroundColor: this.markerColumnColor },
          cellClass: 'asc-visionGrid--markerColumn',
          headerClass: 'asc-visionGrid--markerColumnHeader',
        })
      }
      return newColumnDefs
    },
  },

  watch: {
    isEditable: function (newVal) {
      if (!newVal && !this.selectRowsByDefault) {
        this.agGridOptions.isExternalFilterPresent = () => {
          return true
        }
      } else {
        this.agGridOptions.isExternalFilterPresent = () => {
          return false
        }
      }
      this.agGridOptions.api.onFilterChanged()

      setTimeout(() => {
        // using timeout to avoid zero width in aggrid
        this.agGridOptions.api.sizeColumnsToFit()
      }, 200)
    },
  },

  mounted: function () {
    if (!this.isEditable && !this.selectRowsByDefault) {
      this.agGridOptions.isExternalFilterPresent = () => {
        return true
      }
    } else {
      this.agGridOptions.isExternalFilterPresent = () => {
        return false
      }
    }

    if (this.agGridOptions.api) {
      this.agGridOptions.api.onFilterChanged()
    }
  },
  beforeDestroy() {
    try {
      if (this.gridApi) {
        this.gridApi.destroy()
      }
    } finally {
      this.gridApi = false
    }
    window.removeEventListener('resize', this.handleResizeHandler)
  },

  methods: {
    handleRowSelected(selectedNode) {
      if (this.disabled) return false
      this.$emit('onRowSelected', selectedNode)
    },
    handleSelectionChange(selectedNodes) {
      if (this.disabled) return false
      this.$emit('onSelectionChanged', selectedNodes)
    },
    handleRowDataChanged() {
      if (this.disabled) return false
      if (this.selectRowsByDefault) {
        this.agGridOptions.api.getRenderedNodes().forEach(nodeObj => {
          nodeObj.setSelected(true, false, true)
        })
      }
    },
    checkForInternalUserFilter() {
      return !this.isEditable
    },
    filterSelectedForInternalUser(rowNode) {
      return rowNode.selected
    },
    calculateTextWidth(text, font) {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      context.font = font || '12px Sans-Serif'
      return Math.ceil(context.measureText(text).width) + 32
    },
  },
}
</script>

<style lang="scss">
.asc-vision-grid--holder {
  width: 100%;
  position: relative;

  .asc-vision-grid--disabledOverlay {
    position: absolute;
    z-index: 9999;
    width: 100%;
    height: 100%;
    background: rgba(240, 240, 240, 0.5);
  }

  .ag-theme-balham {
    width: 100%;

    .ag-checkbox-input-wrapper {
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      font-size: $font-size-lrg;
      text-rendering: auto;
      line-height: inherit;
      -webkit-font-smoothing: antialiased;
      position: relative;
      top: -4px;

      &:focus-within,
      &:active {
        outline: none;
        box-shadow: none;
      }
    }

    .ag-icon-menu::before {
      content: '';
    }

    .ag-checkbox-input-wrapper .ag-checkbox-input,
    .ag-checkbox-input-wrapper .ag-checkbox-input.ag-checked {
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      font-size: $font-size-lrg;
      text-rendering: auto;
      line-height: inherit;
      -webkit-font-smoothing: antialiased;
      border: none !important;
      background-color: transparent !important;

      &:hover {
        cursor: pointer;
      }
    }

    .ag-row .asc-vision-grid-icon {
      -webkit-user-select: none;
      -moz-user-select: none;
      user-select: none;
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      font-size: 16px;
      text-rendering: auto;
      line-height: inherit;
      -webkit-font-smoothing: antialiased;

      &::after {
        position: absolute;
      }
    }

    .ag-checkbox-input-wrapper::after,
    .ag-row .asc-vision-grid-icon::after {
      font-family: Material Design Icons, sans-serif;
      content: '\F0209' !important;
      color: $gray-600 !important;
      background-color: initial;
    }

    .ag-checkbox-input-wrapper.ag-checked::after,
    .ag-row.ag-row-selected .asc-vision-grid-icon::after {
      font-family: Material Design Icons, sans-serif;
      content: '\F0208' !important;
      color: $accent-600 !important;
      background-color: initial;
    }

    .ag-checkbox-input-wrapper .ag-icon-checkbox-indeterminate {
      background-color: initial;
    }

    .ag-checkbox-input-wrapper .ag-icon-checkbox-indeterminate::before {
      content: '\F0208' !important;
      color: $accent-600 !important;
      background-color: initial;
    }

    .asc-visionGrid--markerColumn,
    .asc-visionGrid--markerColumnHeader {
      padding: 0;
      border-left: 1px solid #d1d1d1;
    }
  }

  &--disabled {
    .ag-selection-checkbox {
      pointer-events: none;
    }
  }
}
</style>
